import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { apiUrl } from '../../utils/api';
import {
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BanknotesIcon,
  HomeIcon,
  DocumentTextIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import AnnouncementCard from '../announcements/AnnouncementCard';

type MonthOption = {
  value: string;
  label: string;
  startDate?: string;
  endDate?: string;
};

interface DashboardStats {
  overview: {
    totalProperties: number;
    activeProperties: number;
    monthlyOccupancyRate: number;
  };
  monthlyStats: {
    income: number;
    expenses: number;
    netIncome: number;
    reservations: number;
    averageDailyRate: number;
  };
  yearlyStats: {
    income: number;
    expenses: number;
    netIncome: number;
    reservations: number;
  };
  quarterlyStats?: {
    income: number;
    expenses: number;
    netIncome: number;
    label?: string | null;
  };
  period?: {
    monthLabel?: string;
    availableMonths?: MonthOption[];
    selectedMonth?: string;
  };
  reportSummary?: {
    ingresos: number | null;
    gastos: number | null;
    comisionMandato: number | null;
    retenciones: number | null;
    impuestoGenerado: number | null;
    resultadoNetoMandates: number | null;
    valorGiradoAnioAnterior: number | null;
    valorGiradoAnioActual: number | null;
  };
  buildingOptions?: Array<{ id: string; nombre: string }>;
  propertiesByStatus: {
    disponible: number;
    ocupada: number;
    mantenimiento: number;
    reservada: number;
  };
  topProperties: Array<{
    numero: string;
    tipo: string;
    income: number;
  }>;
  financialSummary: {
    totalIngresos: number;
    totalGastos: number;
    balanceNeto: number;
    cantidadTransacciones: number;
    promedioIngreso: number;
    promedioGasto: number;
  };
  portfolio: {
    buildings: string[];
    propertyNames: string[];
    totalValue: number;
    moduleContribution?: {
      propertyId: string;
      room: string;
      percentage: number | null;
      raw: string | null;
      reportId: string | null;
      reportBuilding: string | null;
      reportStartDate: string | null;
      reportEndDate: string | null;
      reportFile: string | null;
    };
  };
  monthOptions?: MonthOption[];
  selectedMonth?: string;
}

interface IncomeChartData {
  month: string;
  income: number;
  reservations: number;
}

interface Property {
  _id: string;
  numero: string;
  edificio?: {
    nombre: string;
    direccion: string;
  };
  tipo: string;
  descripcion: string;
  precioBase: number;
  propietarios: Array<{
    usuario: {
      _id: string;
      nombre: string;
      apellido: string;
    };
    porcentajePropiedad: number;
  }>;
}

interface Announcement {
  _id: string;
  titulo: string;
  contenido: string;
  nivelImportancia: string;
  fechaInicio: string;
  fechaFin: string;
  creadoPor: {
    nombre: string;
    apellido: string;
  };
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [incomeChartData, setIncomeChartData] = useState<IncomeChartData[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [selectedBuilding, setSelectedBuilding] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [monthOptions, setMonthOptions] = useState<MonthOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Cuando cambia selectedBuilding o selectedMonth, recargar datos
  useEffect(() => {
    if (selectedBuilding || selectedMonth) {
      fetchDashboardData(selectedBuilding, selectedMonth);
    }
  }, [selectedBuilding, selectedMonth]);

  const fetchDashboardData = async (buildingId?: string, month?: string) => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const queryParams = new URLSearchParams();
      if (buildingId) queryParams.append('buildingId', buildingId);
      if (month) queryParams.append('month', month);
      const params = queryParams.toString() ? `?${queryParams.toString()}` : '';

      // Cargar datos de manera individual para mejor manejo de errores
      try {
        const statsResponse = await axios.get(apiUrl(`/api/dashboard/stats${params}`), { headers });
        const statsData = statsResponse.data;
        setStats(statsData);
        const apiMonthOptions = statsData?.period?.availableMonths || statsData?.monthOptions || [];
        setMonthOptions(Array.isArray(apiMonthOptions) ? apiMonthOptions : []);
        if (!month && statsData?.period?.selectedMonth) {
          setSelectedMonth(statsData.period.selectedMonth);
        }
        // Preseleccionar sede si solo hay una
        if (!buildingId && statsData?.buildingOptions?.length === 1) {
          const only = statsData.buildingOptions[0].id;
          setSelectedBuilding(only);
          // No llamar recursivo para evitar doble carga; se usará en el select change
        }
      } catch (err: any) {
        console.error('Error cargando estadísticas:', err);
        setError('Error al cargar estadísticas del dashboard');
      }

      try {
        const chartResponse = await axios.get(apiUrl(`/api/dashboard/income-chart${params}`), { headers });
        setIncomeChartData(chartResponse.data);
      } catch (err: any) {
        console.error('Error cargando gráfico:', err);
        // No mostrar error para el gráfico, solo usar datos vacíos
        setIncomeChartData([]);
      }

      try {
        const financialResponse = await axios.get(apiUrl('/api/financial/transactions/summary'), { headers });
        if (financialResponse.data.success) {
          setStats((prev) => prev ? {
            ...prev,
            financialSummary: financialResponse.data.data.summary
          } : prev);
        }
      } catch (err: any) {
        console.error('Error cargando resumen financiero:', err);
        // No mostrar error para el resumen financiero
      }

      try {
        const propertiesResponse = await axios.get(apiUrl('/api/properties/my-properties'), { headers });
        setProperties(propertiesResponse.data.data.properties || []);
      } catch (err: any) {
        console.error('Error cargando propiedades:', err);
        setProperties([]);
      }

      try {
        const announcementsResponse = await axios.get(apiUrl('/api/announcements/user'), { headers });
        setAnnouncements(announcementsResponse.data.data?.announcements || []);
      } catch (err: any) {
        console.error('Error cargando anuncios:', err);
        setAnnouncements([]);
      }

    } catch (err: any) {
      console.error('Error general del dashboard:', err);
      setError('Error al cargar el dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatCurrencyWithDecimals = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getCurrentMonth = () => {
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    const now = selectedMonth ? new Date(`${selectedMonth}-01`) : new Date();
    return months[now.getMonth()];
  };

  const getCurrentYear = () => {
    if (selectedMonth) {
      return parseInt(selectedMonth.split('-')[0], 10);
    }
    return new Date().getFullYear();
  };

  const getQuarterMonths = () => {
    const now = selectedMonth ? new Date(`${selectedMonth}-01`) : new Date();
    const quarter = Math.floor(now.getMonth() / 3);
    const startMonth = quarter * 3;
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return months.slice(startMonth, startMonth + 3);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  // Obtener la primera propiedad para mostrar información del portafolio
  const mainProperty = properties[0];
  const moduleContribution = stats.portfolio.moduleContribution;
  const activeSelectedMonth = selectedMonth || stats.period?.selectedMonth || '';
  const monthLabel = stats.period?.monthLabel
    || monthOptions.find((m) => m.value === activeSelectedMonth)?.label
    || null;

  const formatPercent = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '0%';
    return `${value.toFixed(4)}%`;
  };

  const formatMillions = (value: number, decimals: number = 0) => {
    const millions = value / 1_000_000;
    return `${millions.toFixed(decimals)}M`;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2">Portal Inversionistas</h1>
          <h2 className="text-2xl font-semibold text-gray-600">DASHBOARD</h2>
        </div>
        <div className="flex items-center space-x-4">
          {stats?.buildingOptions && stats.buildingOptions.length > 0 && (
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Sede:</label>
              <select
                value={selectedBuilding}
                onChange={(e) => {
                  setSelectedBuilding(e.target.value);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Todas</option>
                {stats.buildingOptions.map((b) => (
                  <option key={b.id} value={b.id}>{b.nombre}</option>
                ))}
              </select>
            </div>
          )}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Mes:</label>
            <select
              value={activeSelectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {monthOptions.length === 0 && <option value="">Sin datos</option>}
              {monthOptions.length > 0 && monthOptions.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sección Izquierda - Información del Portafolio */}
        <div className="space-y-6">
          {/* Portafolio */}
          <div className="bg-white rounded-lg shadow-md border p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">PORTAFOLIO</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">TOTAL PROPIEDADES:</span>
                <span className="font-semibold">{stats.overview.totalProperties}</span>
              </div>
              {stats.portfolio && (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">SEDE:</span>
                    <span className="font-semibold">
                      {stats.portfolio.buildings.length > 0 ? stats.portfolio.buildings.join(', ') : 'Sin edificio'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">APTO:</span>
                    {stats.portfolio.propertyNames.length === 1 ? (
                      <span className="font-semibold">
                        {stats.portfolio.propertyNames[0]}
                      </span>
                    ) : stats.portfolio.propertyNames.length > 1 ? (
                      <button
                        onClick={() => {
                          window.dispatchEvent(new CustomEvent('navigate', { detail: 'properties' }));
                        }}
                        className="font-semibold text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none p-0"
                      >
                        Ver ({stats.portfolio.propertyNames.length})
                      </button>
                    ) : (
                      <span className="font-semibold">N/A</span>
                    )}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">VR INVERSIÓN:</span>
                    <span className="font-semibold text-green-600 text-sm">${formatCurrency(stats.portfolio.totalValue)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">MÓDULO CONTRIBUCIÓN</span>
                    <span className="font-semibold">
                      {moduleContribution?.percentage !== null && moduleContribution?.percentage !== undefined
                        ? formatPercent(moduleContribution.percentage)
                        : '0%'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">OCUPACIÓN EDIFICIO</span>
                    <span className="font-semibold">
                      {stats.overview.monthlyOccupancyRate !== undefined
                        ? `${stats.overview.monthlyOccupancyRate.toFixed(2)}%`
                        : '0%'}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Resultado Neto y Gastos */}
          <div className="bg-white rounded-lg shadow-md border p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">RESULTADO NETO ACUMULADO</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">TRIMESTRE ({stats.quarterlyStats?.label || getQuarterMonths().join('-').toUpperCase()})</span>
                <span className="text-xl font-bold text-green-600">
                  ${formatCurrency(stats.quarterlyStats?.netIncome ?? stats.yearlyStats.netIncome)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">AÑO ({getCurrentYear()})</span>
                <span className="text-xl font-bold text-green-600">
                  ${formatCurrency(stats.yearlyStats.netIncome)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Valor Girado del Año Anterior</span>
                <span className="text-lg font-semibold">
                  {stats.reportSummary?.valorGiradoAnioAnterior !== null && stats.reportSummary?.valorGiradoAnioAnterior !== undefined
                    ? `$${formatCurrency(stats.reportSummary.valorGiradoAnioAnterior)}`
                    : '—'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Valor Girado del Año Actual</span>
                <span className="text-lg font-semibold">
                  {stats.reportSummary?.valorGiradoAnioActual !== null && stats.reportSummary?.valorGiradoAnioActual !== undefined
                    ? `$${formatCurrency(stats.reportSummary.valorGiradoAnioActual)}`
                    : '—'}
                </span>
              </div>
            </div>
          </div>

          {/* Fotos del Edificio y Apartamento */}
          <div className="bg-white rounded-lg shadow-md border p-6">
            <div className="space-y-4">
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <BuildingOfficeIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">FOTO EDIFICIO</p>
              </div>
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <HomeIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">FOTO APTO {mainProperty?.numero || '1002'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sección Derecha - Tablas y gráfica */}
        <div className="space-y-6">
          {/* Resumen Neto Mensual */}
          {stats.reportSummary && (
            <div className="bg-white rounded-lg shadow-md border p-6 text-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-1">RESUMEN NETO MENSUAL</h3>
              <p className="text-xs text-gray-500 mb-3">
                {monthLabel ? `MES: ${monthLabel}` : ''}
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="py-2 text-gray-600 text-sm">TOTAL INGRESOS</td>
                      <td className="py-2 text-right font-semibold text-green-700 text-sm">
                        {stats.reportSummary.ingresos !== null ? `$${formatCurrency(stats.reportSummary.ingresos)}` : '—'}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 text-gray-600 text-sm">TOTAL GASTOS</td>
                      <td className="py-2 text-right font-semibold text-red-600 text-sm">
                        {stats.reportSummary.gastos !== null ? `$${formatCurrency(stats.reportSummary.gastos)}` : '—'}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 text-gray-600 text-sm">TOTAL COMISION MANDATO</td>
                      <td className="py-2 text-right font-semibold text-gray-800 text-sm">
                        {stats.reportSummary.comisionMandato !== null ? `$${formatCurrency(stats.reportSummary.comisionMandato)}` : '—'}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 text-gray-600 text-sm">TOTAL RETENCIONES</td>
                      <td className="py-2 text-right font-semibold text-gray-800 text-sm">
                        {stats.reportSummary.retenciones !== null ? `$${formatCurrency(stats.reportSummary.retenciones)}` : '—'}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 text-gray-600 text-sm">TOTAL IMPUESTO GENERADO</td>
                      <td className="py-2 text-right font-semibold text-gray-800 text-sm">
                        {stats.reportSummary.impuestoGenerado !== null ? `$${formatCurrency(stats.reportSummary.impuestoGenerado)}` : '—'}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 text-gray-800 font-semibold text-sm">Resultado Neto Mandates</td>
                      <td className="py-2 text-right font-semibold text-green-700 text-sm">
                        {stats.reportSummary.resultadoNetoMandates !== null ? `$${formatCurrency(stats.reportSummary.resultadoNetoMandates)}` : '—'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Gráfico de Resultado Neto Mes a Mes */}
          <div className="bg-white rounded-lg shadow-md border p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">RESULTADO NETO MES A MES</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={incomeChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => value.slice(0, 3).toUpperCase()}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => `$${formatMillions(value, 1)}`}
                  />
                  <Tooltip
                    formatter={(value: number) => [`$${formatCurrency(value)}`, 'Resultado Neto']}
                    labelFormatter={(label) => `Mes: ${label}`}
                  />
                  <Line
                    type="monotone"
                    dataKey="income"
                    stroke="#10B981"
                    strokeWidth={3}
                    dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Sección Inferior */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Anuncios Importantes */}
        <div className="bg-white rounded-lg shadow-md border p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">ANUNCIOS IMPORTANTES</h3>
          <div className="space-y-3">
            {announcements.length > 0 ? (
              announcements.slice(0, 3).map((announcement) => (
                <div key={announcement._id} className="border-l-4 border-blue-500 pl-4 py-2">
                  <h4 className="font-semibold text-gray-900">{announcement.titulo}</h4>
                  <p className="text-sm text-gray-600 mt-1">{announcement.contenido}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Por: {announcement.creadoPor.nombre} {announcement.creadoPor.apellido}
                  </p>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <ExclamationTriangleIcon className="h-8 w-8 mx-auto mb-2" />
                <p>No hay anuncios importantes</p>
              </div>
            )}
          </div>
        </div>

        {/* Acciones Rápidas */}
        <div className="bg-white rounded-lg shadow-md border p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">ACCIONES RÁPIDAS</h3>
          <div className="space-y-3">
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'properties' }))}
              className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2 transition-colors"
            >
              <EyeIcon className="h-5 w-5" />
              <span>VER MIS PROPIEDADES</span>
            </button>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'documents' }))}
              className="w-full bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 flex items-center justify-center space-x-2 transition-colors"
            >
              <DocumentTextIcon className="h-5 w-5" />
              <span>DOCUMENTOS</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;